/* Product tabs only; product carousels continue to use Kalles/Embla. */
const editorProductTabSelection = new Map();

class ODSProductTabs extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    const content = document.getElementById(this.getAttribute('aria-controls'));
    if (!content) return;
    this.buttons = [...this.querySelectorAll('[data-product-tab]')];
    this.panels = [...content.querySelectorAll(':scope > [tab-item]')];
    this.setAttribute('role', 'tablist');
    this.buttons.forEach((button, index) => {
      const panel = this.panels.find(item => item.dataset.productTab === button.dataset.productTab);
      const label = panel?.querySelector('template[data-tab-label]');
      if (!panel || !label) return;
      button.querySelector('.hdt-collection-title-text').replaceChildren(label.content.cloneNode(true));
      button.removeAttribute('aria-label');
      button.id = `${this.id}-tab-${index}`;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panel.id);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', button.id);
      button.addEventListener('click', () => this.select(index), { signal });
      button.addEventListener('keydown', event => {
        const rtl = getComputedStyle(this).direction === 'rtl';
        const delta = event.key === 'ArrowRight' ? (rtl ? -1 : 1) : event.key === 'ArrowLeft' ? (rtl ? 1 : -1) : 0;
        if (!delta && event.key !== 'Home' && event.key !== 'End') return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.buttons.length - 1 : (index + delta + this.buttons.length) % this.buttons.length;
        this.select(next);
        this.buttons[next].focus();
      }, { signal });
    });
    this.addEventListener('shopify:block:select', event => {
      const index = this.buttons.findIndex(button => button.contains(event.target));
      if (index >= 0) this.select(index);
    }, { signal });
    content.addEventListener('shopify:block:select', event => {
      const panel = this.panels.find(item => item.contains(event.target));
      const index = this.buttons.findIndex(button => button.dataset.productTab === panel?.dataset.productTab);
      if (index >= 0) this.select(index);
    }, { signal });
    let remembered;
    if (window.Shopify?.designMode) {
      remembered = editorProductTabSelection.get(this.id);
      try { remembered = sessionStorage.getItem(`ods:editor-product-tab:${this.id}`) || remembered; } catch { /* Storage can be unavailable in an embedded editor. */ }
    }
    const rememberedIndex = this.buttons.findIndex(button => button.dataset.productTab === remembered);
    const initialIndex = rememberedIndex >= 0 ? rememberedIndex : this.buttons.findIndex(button => button.getAttribute('aria-current') === 'true');
    this.select(Math.max(0, initialIndex), false);
  }

  select(index, activate = true) {
    if (window.Shopify?.designMode && this.buttons[index]) {
      const block = this.buttons[index].dataset.productTab;
      editorProductTabSelection.set(this.id, block);
      try { sessionStorage.setItem(`ods:editor-product-tab:${this.id}`, block); } catch { /* Keep the in-memory fallback. */ }
    }
    this.buttons.forEach((button, current) => {
      const selected = current === index;
      button.setAttribute('aria-selected', String(selected));
      button.setAttribute('aria-current', String(selected));
      button.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;
      if (selected) {
        const template = panel.querySelector(':scope > template[data-product-tab-content]');
        if (template) template.replaceWith(template.content.cloneNode(true));
      }
      panel.hidden = !selected;
      panel.toggleAttribute('tab-selected', selected);
      if (selected && activate) panel.querySelectorAll('ods-product-carousel').forEach(carousel => carousel.initialize?.());
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

if (!customElements.get('ods-product-tabs')) customElements.define('ods-product-tabs', ODSProductTabs);
